import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets } from "lucide-react";
import { Tilt3D } from "./Motion";
import { GlowFrame } from "./GlowFrame";
import { Reveal } from "./Reveal";
import { LiveIcon } from "./LiveIcon";
import { GhostCTA } from "./CTA";
import { SITE } from "../data/site";
import { useLang } from "../i18n";

// Photo card with a cursor-tracked specular "wet gloss" + Tilt3D + gel sink
// (via GlowFrame). Photos: CC0 / CC-BY (Wikimedia Commons & Flickr, credit in alt).
const GlossCard = ({ src, alt, testid, className = "", imgClass = "", tone = "cyan", bubbles = false }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--gx", `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
    el.style.setProperty("--gy", `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
  };
  return (
    <Tilt3D className={className} max={8}>
      <GlowFrame tone={tone}>
        <div ref={ref} onMouseMove={onMove} data-testid={testid} className="wet-photo group/wet relative overflow-hidden rounded-[18px]">
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={`h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/wet:scale-[1.06] ${imgClass}`}
          />
          <span className="wet-gloss" aria-hidden="true" />
          {bubbles &&
            [0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                aria-hidden="true"
                className="bubble-rise"
                style={{ left: `${12 + i * 18}%`, animationDelay: `${i * 1.7}s`, animationDuration: `${7 + i * 1.3}s` }}
              />
            ))}
        </div>
      </GlowFrame>
    </Tilt3D>
  );
};

// "Water damage rescue" promo — real water-submerged phone photography,
// fully interactive. Copy reuses the existing translated water-damage
// service text so all 6 languages stay truthful.
export const WaterRescue = () => {
  const { t } = useLang();
  const water = t("lab.services.water");
  return (
    <section
      data-testid="water-rescue-section"
      className="grain relative overflow-hidden border-y border-sand/60 bg-espresso"
      aria-label={water.title}
    >
      <div className="pointer-events-none absolute -top-28 right-[8%] h-80 w-80 rounded-full bg-cyan/15 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-[-10%] left-[4%] h-72 w-72 rounded-full bg-eblue/15 blur-3xl" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-10 lg:px-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow !text-cyan flex items-center gap-2">
              <Droplets className="h-4 w-4" />
              {t("lab.eyebrow")}
              <LiveIcon variant="eq" color="#4FB3BF" className="!h-7 !w-7 !rounded-lg" />
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="depth-3d mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12]">
              {water.title}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">{water.blurb}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-6 flex flex-wrap gap-2">
              {water.symptoms.map((s) => (
                <span key={s} className="rounded-full border border-cyan/25 bg-cyan/10 px-3 py-1.5 text-xs text-ink/80">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/quote?issue=water"
                data-testid="water-quote-cta"
                className="sink-target group inline-flex items-center gap-2.5 rounded-full bg-cyan px-7 py-3.5 text-sm sm:text-base font-bold uppercase tracking-wide text-[#0B111B] transition-all duration-300 hover:bg-volt"
              >
                {t("lab.ask")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
              </Link>
              <GhostCTA href={SITE.phoneHref} testid="water-call-cta" dark>
                {t("lab.ctaCall")} {SITE.phoneDisplay}
              </GhostCTA>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            <Reveal className="col-span-2">
              <GlossCard
                src="/media/wet-phone-wide.jpg"
                alt="Smartphone covered in water droplets in a sink — photo by Dylandabi, CC BY, Wikimedia Commons"
                testid="water-photo-main"
                className="aspect-[16/9]"
                bubbles
              />
            </Reveal>
            <Reveal delay={0.12}>
              <GlossCard
                src="/media/wet-phone-drops.jpg"
                alt="Wet smartphone with large water drops on its screen — photo by Dylandabi, CC BY, Wikimedia Commons"
                testid="water-photo-drops"
                className="aspect-[3/4]"
              />
            </Reveal>
            <div className="grid gap-4 sm:gap-6">
              <Reveal delay={0.18}>
                <GlossCard
                  src="/media/shattered-phone.jpg"
                  alt="Shattered phone screen with spiderweb cracks — CC0, Wikimedia Commons"
                  testid="water-photo-shattered"
                  className="aspect-[4/3]"
                  tone="clay"
                />
              </Reveal>
              <Reveal delay={0.24}>
                <GlossCard
                  src="/media/broken-laptop.jpg"
                  alt="Laptop with a broken, glitching display — photo by a Flickr contributor, CC BY"
                  testid="water-photo-laptop"
                  className="aspect-[4/3]"
                  tone="volt"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
